import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface CustomSelectOption {
  value: string | number;
  label: string;
}

interface CustomSelectProps {
  value: string | number;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  placeholder?: string;
  icon?: React.ReactNode;
  className?: string;
  buttonClassName?: string;
  align?: 'left' | 'right';
  disabled?: boolean;
}

export function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Seleccione...',
  icon,
  className = '',
  buttonClassName = '',
  align = 'left',
  disabled = false,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Safe string coercion comparison to handle number vs string values properly
  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const alignmentClass = align === 'right' ? 'right-0 min-w-full' : 'left-0 min-w-full';

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-10 flex items-center justify-between gap-1.5 rounded-xl border border-border bg-surface px-3 text-xs font-semibold text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 shadow-xs transition-all text-left btn-press disabled:opacity-60 disabled:cursor-not-allowed ${buttonClassName}`}
      >
        <span className="flex items-center gap-1.5 truncate">
          {icon}
          <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-text-tertiary transition-transform duration-200 shrink-0 ${
            isOpen ? 'rotate-180 text-brand-orange' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute ${alignmentClass} mt-1.5 max-h-60 overflow-y-auto rounded-xl border border-border bg-surface shadow-xl z-50 p-1 space-y-0.5 animate-scale-in custom-scrollbar`}
        >
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  onChange(String(opt.value));
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-brand-orange/10 text-brand-orange font-bold'
                    : 'text-text-primary hover:bg-surface-tertiary'
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && <Check className="h-3.5 w-3.5 text-brand-orange shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

